import {useState} from 'react';
import {Button, Select, Tag} from 'antd';
import DBModal from 'components/modal/modal';
import {countryISOs} from 'graphql/queries/trendingCountries';
import {KYC_STATUS_OPTIONS} from './kycQueueShared';
import styles from './kycDashboard.module.scss';

/**
 * The attribute filters the manual and automatic queues share: country in the
 * row itself, and document type, region, document nationality and document
 * nationality region behind the Filters button.
 *
 * Country is out in the row because it is the one reviewers reach for
 * constantly, and a filter you use on every pass should not cost two clicks
 * and a modal. The other four stay behind the button: they are occasional, and
 * putting all five in the row turned it into a wall of dropdowns that pushed
 * Refresh off the end of it.
 *
 * The two country fields are kept apart on purpose — side by side with nothing
 * between them they read as the same control twice, so the second one lives in
 * the modal under a label that says it is the document's own nationality.
 *
 * Different filters are AND-ed by the server and the values inside one are
 * OR-ed. An empty selection is sent as nothing at all, which the server reads
 * as "all".
 */

export const EMPTY_QUEUE_FILTERS = {
  documentTypes: [],
  countryCodes: [],
  regions: [],
  documentNationalityCountryCodes: [],
  documentNationalityRegions: [],
};

// KycDocumentType. `idCard` and `license` are missing on purpose — they are the
// legacy spellings of two of the types below, not types of their own, so they
// are folded into those options rather than offered twice.
export const KYC_DOCUMENT_TYPE_OPTIONS = [
  {value: 'passport', label: 'Passport'},
  {value: 'national_identity_card', label: 'National identity card'},
  {value: 'residence_permit', label: 'Residence permit'},
  {value: 'driving_license', label: 'Driving license'},
  {value: 'provisional_driving_license', label: 'Provisional driving license'},
  {value: 'visa', label: 'Visa'},
  {value: 'travel_document', label: 'Travel document'},
  {value: 'work_permit', label: 'Work permit'},
  {value: 'ssn', label: 'Social security number'},
  {value: 'social_id', label: 'Social ID'},
  {value: 'citizen_id', label: 'Citizen ID'},
  {value: 'voter_id', label: 'Voter ID'},
  {value: 'tax_id', label: 'Tax ID'},
  {value: 'health_card', label: 'Health card'},
  {value: 'address_card', label: 'Address card'},
  {value: 'alien_card', label: 'Alien card'},
  {value: 'tribal_card', label: 'Tribal card'},
  {value: 'occupation_card', label: 'Occupation card'},
  {value: 'uniformed_services_card', label: 'Uniformed services card'},
  {value: 'seamans_id', label: 'Seaman’s ID'},
  {value: 'registration_certificate', label: 'Registration certificate'},
  {value: 'vehicle_particulars', label: 'Vehicle particulars'},
  {value: 'trade_license', label: 'Trade license'},
];

// RegionCategory, as the KYC queues accept it. This is not
// REGION_CATEGORY_FILTER_OPTIONS from utils/constants: that list is what
// getAllUsers takes, and it groups Africa differently — sending its
// CENTRAL_WEST_AFRICA here would be rejected.
export const KYC_REGION_OPTIONS = [
  {value: 'NORTH_AMERICA', label: 'North America'},
  {value: 'SOUTH_AMERICA', label: 'South America'},
  {value: 'EUROPE', label: 'Europe'},
  {value: 'NORTH_AFRICA', label: 'North Africa'},
  {value: 'WEST_AFRICA', label: 'West Africa'},
  {value: 'CENTRAL_AFRICA', label: 'Central Africa'},
  {value: 'EAST_AFRICA', label: 'East Africa'},
  {value: 'SOUTH_AFRICA', label: 'Southern Africa'},
  {value: 'WAEMU', label: 'WAEMU'},
  {value: 'REST_OF_WORLD', label: 'Rest of world'},
];

const FILTER_FIELDS = [
  {
    key: 'documentTypes',
    label: 'Document type',
    placeholder: 'All document types',
    hint: 'The type recorded on the identity card, or the one the provider sent back.',
    options: KYC_DOCUMENT_TYPE_OPTIONS,
  },
  {
    key: 'regions',
    label: 'Region',
    placeholder: 'All regions',
    hint: 'Worked out from the profile country filtered in the row above. WAEMU countries also match West Africa.',
    options: KYC_REGION_OPTIONS,
  },
  {
    key: 'documentNationalityCountryCodes',
    label: 'Document nationality',
    placeholder: 'All nationalities',
    hint: 'The country the KYC document itself is for, which is not always where the user lives. It never falls back to the profile country.',
    options: countryISOs,
  },
  {
    key: 'documentNationalityRegions',
    label: 'Document nationality region',
    placeholder: 'All nationality regions',
    hint: 'The region of that document nationality.',
    options: KYC_REGION_OPTIONS,
  },
];

const asList = values => (values?.length ? values : undefined);

/** Empty values for the modal's own fields, leaving the row's country alone. */
const emptyModalFilters = () =>
  Object.fromEntries(FILTER_FIELDS.map(field => [field.key, []]));

// The two legacy spellings, sent alongside the type they are a spelling of. The
// server matches known aliases itself, so this is belt and braces — but values
// in one list are OR-ed, so an alias can only ever add cards of the type that
// was already selected, and legacy cards stay in the results either way.
const DOCUMENT_TYPE_ALIASES = {
  national_identity_card: 'idCard',
  driving_license: 'license',
};

const withDocumentTypeAliases = values =>
  values?.length
    ? values.flatMap(value =>
        DOCUMENT_TYPE_ALIASES[value]
          ? [value, DOCUMENT_TYPE_ALIASES[value]]
          : [value],
      )
    : undefined;

/**
 * The five fields as the server takes them. Every key is always present so the
 * query variables keep one shape; an unselected filter is `undefined`, which is
 * dropped on the way out and read as "all".
 */
export const buildKycFilterInput = (filters = EMPTY_QUEUE_FILTERS) => ({
  documentTypes: withDocumentTypeAliases(filters?.documentTypes),
  countryCodes: asList(filters?.countryCodes),
  regions: asList(filters?.regions),
  documentNationalityCountryCodes: asList(
    filters?.documentNationalityCountryCodes,
  ),
  documentNationalityRegions: asList(filters?.documentNationalityRegions),
});

/** How many of the five are narrowing the queue. */
export const countSelectedKycFilters = (filters = EMPTY_QUEUE_FILTERS) =>
  FILTER_FIELDS.filter(field => filters?.[field.key]?.length).length;

const labelFor = (options, value) =>
  options.find(option => option.value === value)?.label || value;

const KycQueueFilterForm = ({value, onApply, onClose}) => {
  const [draft, setDraft] = useState(value || EMPTY_QUEUE_FILTERS);
  const selected = countSelectedKycFilters(draft);

  return (
    <div className={styles.filterModal}>
      <div className={styles.filterModalHeader}>
        <h1>Filter the queue</h1>
        <p>
          Each field narrows the queue further, and picking several values in
          one field widens it. Leave a field empty for all.
        </p>
      </div>

      <div className={styles.filterFields}>
        {FILTER_FIELDS.map(field => (
          <div className={styles.filterField} key={field.key}>
            <label htmlFor={`kyc-filter-${field.key}`}>{field.label}</label>
            <Select
              id={`kyc-filter-${field.key}`}
              mode="multiple"
              allowClear
              showSearch
              optionFilterProp="label"
              maxTagCount="responsive"
              className={styles.filterSelect}
              placeholder={field.placeholder}
              options={field.options}
              value={draft[field.key]}
              onChange={next =>
                setDraft(current => ({...current, [field.key]: next || []}))
              }
            />
            <p className={styles.filterFieldHint}>{field.hint}</p>
          </div>
        ))}
      </div>

      <div className={styles.filterModalFooter}>
        <button
          type="button"
          className={styles.filterClear}
          onClick={() =>
            // Only the fields this modal owns. Country is chosen out in the
            // row and clearing it from in here would look like a glitch.
            setDraft(current => ({...current, ...emptyModalFilters()}))
          }>
          Clear all
        </button>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          type="primary"
          className={styles.filterApply}
          onClick={() => onApply(draft)}>
          {selected
            ? `Apply ${selected} filter${selected === 1 ? '' : 's'}`
            : 'Apply'}
        </Button>
      </div>
    </div>
  );
};

/**
 * The button that opens the five filters, for the queue's action row.
 *
 * Nothing is refetched while the modal is open — the queue reloads once, on
 * Apply, rather than once per dropdown.
 */
export const KycQueueFilters = ({value, onChange}) => {
  const [isOpen, setIsOpen] = useState(false);
  const selected = countSelectedKycFilters(value);

  return (
    <>
      <Button
        onClick={() => setIsOpen(true)}
        className={selected ? styles.filterButtonActive : undefined}>
        {selected ? `Filters (${selected})` : 'Filters'}
      </Button>
      <DBModal
        isOpen={isOpen}
        handleClose={() => setIsOpen(false)}
        width={640}
        destroyOnClose
        content={
          <KycQueueFilterForm
            value={value}
            onClose={() => setIsOpen(false)}
            onApply={next => {
              onChange(next);
              setIsOpen(false);
            }}
          />
        }
      />
    </>
  );
};

/**
 * What is currently being filtered on, spelled out under the action row.
 *
 * Without this the only sign that a queue is showing a slice of itself is a
 * number on a button, and an empty table reads as an empty queue.
 */
export const KycQueueFilterSummary = ({value, onChange}) => {
  const active = FILTER_FIELDS.filter(field => value?.[field.key]?.length);
  if (!active.length) return null;

  return (
    <div className={styles.activeFilters}>
      <span className={styles.activeFiltersLabel}>Filtered by</span>
      {active.map(field => {
        const values = value[field.key];
        const shown = values
          .slice(0, 2)
          .map(item => labelFor(field.options, item))
          .join(', ');
        const rest = values.length - 2;

        return (
          <Tag
            key={field.key}
            closable
            className={styles.activeFilterTag}
            onClose={() => onChange({...value, [field.key]: []})}>
            {field.label}: {shown}
            {rest > 0 ? ` +${rest}` : ''}
          </Tag>
        );
      })}
      <button
        type="button"
        className={styles.filterClear}
        onClick={() => onChange({...value, ...emptyModalFilters()})}>
        Clear all
      </button>
    </div>
  );
};

/* -------------------------------------------------------------------------
 * The four status selectors all three tabs carry.
 *
 * The identity, document and AML checks are independent of each other, so they
 * are three separate lists rather than one status: a card can be an approved
 * identity check with a rejected AML screening, and one dropdown cannot say
 * that. Deposit status is the fourth because "approved KYC, deposit stuck in
 * PENDING" is the group support actually goes looking for.
 *
 * These sit in the action row rather than behind the Filters button — they are
 * what a reviewer changes every few minutes, unlike the five attribute filters.
 * ---------------------------------------------------------------------- */

// WalletTransactionStatus, narrowed to the three that mean anything for a
// deposit. The enum also has CREATED and CANCELLED; nobody filters a KYC queue
// by them, and they are not offered rather than being silently dropped.
//
// Bare status words, not "Deposit pending": the column is labelled Deposit, and
// the longer wording made a tag that no longer fit its own box.
export const DEPOSIT_STATUS_OPTIONS = [
  {value: 'PENDING', label: 'Pending'},
  {value: 'SUCCESSFUL', label: 'Successful'},
  {value: 'FAILED', label: 'Failed'},
];

export const EMPTY_CHECK_FILTERS = {
  kycStatuses: [],
  documentCheckStatuses: [],
  amlStatuses: [],
  depositStatuses: [],
};

// The placeholder is just "All": the label above the box already says which
// check this is, and a placeholder that repeats it only makes the box wider.
const CHECK_FILTER_FIELDS = [
  {key: 'kycStatuses', label: 'KYC statuses', options: KYC_STATUS_OPTIONS},
  {
    key: 'documentCheckStatuses',
    label: 'Document check',
    options: KYC_STATUS_OPTIONS,
  },
  {key: 'amlStatuses', label: 'AML', options: KYC_STATUS_OPTIONS},
  {
    key: 'depositStatuses',
    label: 'Deposit',
    options: DEPOSIT_STATUS_OPTIONS,
  },
];

/**
 * The four lists as the server takes them, for `filters.user` on the dashboard
 * and `filters` on both queues — the field names are the same on all three.
 *
 * An empty selection is `undefined`, which is dropped on the way out and read
 * as "all". Nothing here ever sends the legacy singular `kycStatus`: supplying
 * both narrows the queue twice, and a pair that disagrees returns nothing.
 */
export const buildCheckFilterInput = (filters = EMPTY_CHECK_FILTERS) => ({
  kycStatuses: asList(filters?.kycStatuses),
  documentCheckStatuses: asList(filters?.documentCheckStatuses),
  amlStatuses: asList(filters?.amlStatuses),
  depositStatuses: asList(filters?.depositStatuses),
});

/**
 * One labelled, fixed-width column in a filter row.
 *
 * The fixed width is load-bearing for the multi-selects: they grow with every
 * tag they hold, and a row of boxes that each resize as they fill shunts its
 * neighbours sideways while a reviewer is still clicking through them. The
 * label is load-bearing too — once a box is full of tags its placeholder is
 * gone, which would leave three identical-looking dropdowns with nothing to
 * say which check is which.
 *
 * Every control in a filter row goes through this, selects and date pickers
 * alike, so they line up on one baseline instead of each sizing itself.
 */
export const QueueFilterField = ({label, htmlFor, wide, children}) => (
  <div
    className={`${styles.queueField} ${
      wide ? styles.queueFieldWide : ''
    }`.trim()}>
    <label htmlFor={htmlFor}>{label}</label>
    {children}
  </div>
);

/**
 * Country, in the filter row rather than behind the Filters button.
 *
 * Wider than the status columns and searchable: there are around two hundred
 * options with long names, and a reviewer filtering a queue by country is
 * usually typing the name rather than scrolling for it. It still collapses
 * extra selections into a +N count, so picking six countries does not stretch
 * the row.
 *
 * Emits a patch, like the status selectors, so it can only ever change its own
 * field — the other four attribute filters are edited in the modal and must
 * not be echoed back from a stale render.
 */
export const KycCountryFilter = ({value, onChange}) => (
  <QueueFilterField label="Country" htmlFor="kyc-filter-country" wide>
    <Select
      id="kyc-filter-country"
      mode="multiple"
      allowClear
      showSearch
      optionFilterProp="label"
      maxTagCount="responsive"
      placeholder="All countries"
      options={countryISOs}
      value={value?.countryCodes || []}
      onChange={next => onChange({countryCodes: next || []})}
    />
  </QueueFilterField>
);

/**
 * Country, for the dashboard tab.
 *
 * Single-select, unlike the queues': the dashboard filters through
 * `UserFilters`, which exposes `country: String` and no list equivalent, so
 * offering a multi-select here would let a reviewer pick five countries and
 * silently filter by one. Same options and same column as the queue version,
 * so the row still reads the same.
 *
 * Note it is not quite the same country either — this one is the country of
 * citizenship on the KYC document, not the profile country the queues match.
 */
export const KycUserCountryFilter = ({value, onChange}) => (
  <QueueFilterField label="Country" htmlFor="kyc-user-country" wide>
    <Select
      id="kyc-user-country"
      allowClear
      showSearch
      optionFilterProp="label"
      placeholder="All countries"
      options={countryISOs}
      value={value?.country || undefined}
      onChange={next => onChange({country: next || ''})}
    />
  </QueueFilterField>
);

/**
 * The four status selectors, for a tab's filter row.
 *
 * `onChange` is handed a patch — `{amlStatuses: [...]}` — not the whole filter
 * object, so a selector can only ever change its own field. Echoing the other
 * three back would let a stale render overwrite a change made since.
 *
 * `maxTagCount="responsive"` collapses whatever does not fit into a +N count
 * rather than widening the box.
 */
export const KycCheckFilters = ({value, onChange}) =>
  CHECK_FILTER_FIELDS.map(field => (
    <QueueFilterField
      key={field.key}
      label={field.label}
      htmlFor={`kyc-check-${field.key}`}>
      <Select
        id={`kyc-check-${field.key}`}
        mode="multiple"
        showArrow
        allowClear
        maxTagCount="responsive"
        placeholder="All"
        options={field.options}
        value={value?.[field.key] || []}
        onChange={next => onChange({[field.key]: next || []})}
      />
    </QueueFilterField>
  ));
